import * as React from 'react'
import { cn } from '@/lib/utils'

const colors = {
  default: 'bg-enterprise-indigo text-enterprise-on-accent border-transparent',
  secondary: 'bg-enterprise-surface text-enterprise-muted border-enterprise-border',
  success: 'bg-enterprise-success/15 text-enterprise-success border-enterprise-success/20',
  warning: 'bg-enterprise-warning/15 text-enterprise-warning border-enterprise-warning/20',
  danger: 'bg-enterprise-danger/15 text-enterprise-danger border-enterprise-danger/20',
  info: 'bg-enterprise-indigo/15 text-enterprise-indigo border-enterprise-indigo/20',
  outline: 'border border-enterprise-border text-enterprise-muted bg-enterprise-bg',
} as const

export type BadgeVariant = keyof typeof colors

export function Badge({
  variant = 'default',
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { variant?: BadgeVariant }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-xs font-medium tracking-tight',
        colors[variant],
        className,
      )}
      {...props}
    />
  )
}

export function normalizeState(label: string): string {
  return label.toLowerCase().trim().replace(/[-_\s]+/g, '_').replace(/^_+|_+$/g, '')
}

// Decorative state fills (chart bars, legend dots) — resolve to the -solid role
// tokens so inline styles track the theme (no duplicate literals).
export const STATE_COLOR: Record<BadgeVariant, string> = {
  success: 'rgb(var(--enterprise-success-solid))',
  danger: 'rgb(var(--enterprise-danger-solid))',
  warning: 'rgb(var(--enterprise-warning-solid))',
  info: 'rgb(var(--enterprise-indigo-solid))',
  secondary: 'var(--enterprise-muted)',
  default: 'rgb(var(--enterprise-indigo-solid))',
  outline: 'var(--enterprise-muted)',
}

export type StateDomain = 'deployment' | 'training' | 'eval' | 'contract' | 'balance' | 'project'

type Translator = (key: string, opts?: Record<string, unknown>) => string

// canonical single source — variant + i18n labelKey (+ optional hintKey), tagged with domain.
// 同名状态跨域分词：deployment.failed =「部署失败」，training.failed =「训练失败」（失败≠未就绪）；
// deployment.provisioning =「正在准备端点…」，training.provisioning =「资源准备中…」。
const STATE_CONFIG: Record<string, { variant: BadgeVariant; labelKey: string; hintKey?: string }> = {
  // deployment 9
  creating: { variant: 'info', labelKey: 'state.deployment.creating' },
  provisioning: { variant: 'info', labelKey: 'state.deployment.provisioning' },
  loading: { variant: 'info', labelKey: 'state.deployment.loading' },
  ready: { variant: 'success', labelKey: 'state.deployment.ready' },
  idle: { variant: 'success', labelKey: 'state.deployment.idle' },
  scaling_down: { variant: 'warning', labelKey: 'state.deployment.scaling_down' },
  scaled_to_zero: { variant: 'warning', labelKey: 'state.deployment.scaled_to_zero' },
  cold_starting: { variant: 'info', labelKey: 'state.deployment.cold_starting', hintKey: 'state.hint.coldStartRetry' },
  failed: { variant: 'danger', labelKey: 'state.deployment.failed', hintKey: 'state.hint.viewReason' },
  // training 10 — failed 走 failed_training；已取消为中性次级（用户主动行为，非故障）
  // provisioning 同名跨域：训练任务等的是「资源准备中」，不是部署的「正在准备端点…」。
  queued: { variant: 'info', labelKey: 'state.training.queued' },
  provisioning_training: { variant: 'info', labelKey: 'state.training.provisioning' },
  initializing: { variant: 'info', labelKey: 'state.training.initializing' },
  running: { variant: 'info', labelKey: 'state.training.running' },
  checkpointing: { variant: 'info', labelKey: 'state.training.checkpointing' },
  finalizing: { variant: 'info', labelKey: 'state.training.finalizing' },
  completed: { variant: 'success', labelKey: 'state.training.completed' },
  cancelling: { variant: 'warning', labelKey: 'state.training.cancelling' },
  cancelled: { variant: 'secondary', labelKey: 'state.training.cancelled' },
  failed_training: { variant: 'danger', labelKey: 'state.training.failed', hintKey: 'state.hint.viewReason' },
  // model lifecycle extras — 'active' is the model's steady state (versions
  // never carry it; balances resolve via domain-prefixed keys)
  active: { variant: 'success', labelKey: 'state.model.active' },
  // model version extras — share palette, not new colors
  available: { variant: 'success', labelKey: 'state.version.available' },
  validating: { variant: 'info', labelKey: 'state.version.validating' },
  deleted: { variant: 'danger', labelKey: 'state.version.deleted' },
  deprecated: { variant: 'warning', labelKey: 'state.version.deprecated' },
  // serverless estimate statuses (gpu-sizing serverlessStatusSchema)
  queuing: { variant: 'info', labelKey: 'state.serverless.queuing' },
  needs_attention: { variant: 'warning', labelKey: 'state.serverless.needs_attention' },
  undeployable: { variant: 'danger', labelKey: 'state.serverless.undeployable' },
  // eval run 4-state (§6.3) — 同名状态跨域分词：eval 域走 eval_* 前缀（见 resolveConfig）
  eval_queued: { variant: 'info', labelKey: 'state.eval.queued' },
  eval_running: { variant: 'info', labelKey: 'state.eval.running' },
  eval_completed: { variant: 'success', labelKey: 'state.eval.completed' },
  eval_failed: { variant: 'danger', labelKey: 'state.eval.failed', hintKey: 'state.hint.viewReason' },
  // contract order 4-state (ADR-0030) — contract_* 前缀域分词
  contract_draft: { variant: 'secondary', labelKey: 'state.contract.draft' },
  contract_confirmed: { variant: 'info', labelKey: 'state.contract.confirmed' },
  contract_settled: { variant: 'success', labelKey: 'state.contract.settled' },
  contract_cancelled: { variant: 'secondary', labelKey: 'state.contract.cancelled' },
  // prepaid balance 3-state — balance_* 前缀域分词
  balance_active: { variant: 'success', labelKey: 'state.balance.active' },
  balance_expired: { variant: 'warning', labelKey: 'state.balance.expired' },
  balance_breakage: { variant: 'secondary', labelKey: 'state.balance.breakage' },
  // client project 2-state (gh #199) — project_* 前缀域分词
  project_active: { variant: 'success', labelKey: 'state.project.active' },
  project_archived: { variant: 'secondary', labelKey: 'state.project.archived' },
}

function resolveConfig(normalized: string, domain?: StateDomain) {
  // Prefixed domains resolve via `<domain>_<state>` (eval/contract/balance —
  // run/order/pool states share words with deployments but read differently).
  if (domain && domain !== 'deployment' && domain !== 'training') {
    const domainCfg = STATE_CONFIG[`${domain}_${normalized}`]
    if (domainCfg) return domainCfg
  }
  if (normalized === 'failed') {
    // failed 本身无域倾向：显式传 domain 时按域取词，未传时按部署域（同名冲突状态之一，另一处是 provisioning）
    return STATE_CONFIG[domain === 'training' ? 'failed_training' : 'failed']
  }
  if (normalized === 'provisioning') {
    // 同名跨域的第二处：部署域「正在准备端点…」vs 训练域「资源准备中…」。
    // 训练任务的 wire state 里确实有 provisioning（见 src/lib/states.ts TRAINING_ACTIVE_STATES），
    // 不按域分词 = 一条在等资源的训练任务挂着部署的措辞。
    return STATE_CONFIG[domain === 'training' ? 'provisioning_training' : 'provisioning']
  }
  return STATE_CONFIG[normalized]
}

export function stateBadge(state: string, domain?: StateDomain): BadgeVariant {
  return resolveConfig(normalizeState(state), domain)?.variant ?? 'secondary'
}

export function stateColorHex(state: string): string {
  const v = stateBadge(state)
  return STATE_COLOR[v] ?? STATE_COLOR.secondary
}

/**
 * 人话状态标签（域感知 + i18n）。
 * - `t` 未传时回退为原始状态串（仅兜底场景）。
 * - `domain` 决定跨域状态（failed / provisioning）用哪套词：训练任务传 'training' →
 *   「训练失败」/「资源准备中…」。
 * - hint 为补充说明（如「查看原因」），调用方应渲染成正常尺寸的可点链接，不再用 10px 微字。
 */
export function humanStateLabel(
  state: string,
  opts?: { t?: Translator; domain?: StateDomain },
): { label: string; hint: string } {
  const n = normalizeState(state)
  const cfg = resolveConfig(n, opts?.domain)
  if (!opts?.t || !cfg) return { label: state, hint: '' }
  return {
    label: opts.t(cfg.labelKey, { defaultValue: state }),
    hint: cfg.hintKey ? String(opts.t(cfg.hintKey, { defaultValue: '' })) : '',
  }
}
